import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Download, FileText, ListChecks, GraduationCap, MessageSquareQuote, Bookmark, PlayCircle, RefreshCw, Trash2, Plus, Loader2 } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { api, errMsg, runJob } from "../lib/api";

async function downloadPdf(paperId, docType, subject) {
  try {
    const res = await api.get(`/papers/${paperId}/pdf`, { params: { doc: docType }, responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${subject.replace(/\s+/g, "_")}_${docType}.pdf`;
    a.click();
    URL.revokeObjectURL(url);
  } catch (e) {
    toast.error("Could not download PDF");
  }
}

export const PaperViewer = ({ paper, editable = false, onChange }) => {
  const [bookmarked, setBookmarked] = useState(paper.bookmarked);
  const [busy, setBusy] = useState(null); // question number being replaced/removed
  const [adding, setAdding] = useState(false);
  const topicList = Array.from(new Set((paper.questions || []).map((q) => q.topic).filter(Boolean)));
  const [addTopic, setAddTopic] = useState(topicList[0] || "");
  const [addMarks, setAddMarks] = useState("4");
  const [topicOptions, setTopicOptions] = useState(topicList);

  useEffect(() => {
    if (!editable) return;
    api.get("/topics").then((r) => {
      const names = (r.data || []).map((t) => t.topic).filter(Boolean);
      const merged = Array.from(new Set([...names, ...topicList]));
      if (merged.length) {
        setTopicOptions(merged);
        setAddTopic((cur) => cur || merged[0]);
      }
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editable]);
  const byNum = (arr) => Object.fromEntries((arr || []).map((m) => [m.number, m]));
  const ms = byNum(paper.mark_scheme);
  const ma = byNum(paper.model_answers);
  const ec = byNum(paper.examiner_commentary);

  const refetch = async (data) => {
    if (!onChange) return;
    await onChange(data);
  };

  const replaceQuestion = async (number) => {
    setBusy(number);
    try {
      await runJob(() => api.post(`/papers/${paper.id}/questions/${number}/replace`));
      await refetch();
      toast.success("New question generated");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(null);
    }
  };

  const removeQuestion = async (number) => {
    setBusy(number);
    try {
      const { data } = await api.delete(`/papers/${paper.id}/questions/${number}`);
      await refetch(data);
      toast.success("Question removed — add a replacement to keep your paper full");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(null);
    }
  };

  const addQuestion = async () => {
    if (!addTopic) { toast.error("Choose a topic"); return; }
    setAdding(true);
    try {
      await runJob(() => api.post(`/papers/${paper.id}/questions/add`, { topic: addTopic, marks: parseInt(addMarks, 10) }));
      await refetch();
      toast.success("Question added");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setAdding(false);
    }
  };

  const toggleBookmark = async () => {
    try {
      const { data } = await api.patch(`/papers/${paper.id}/bookmark`);
      setBookmarked(data.bookmarked);
      toast.success(data.bookmarked ? "Paper bookmarked" : "Bookmark removed");
    } catch (e) {
      toast.error("Could not update bookmark");
    }
  };

  return (
    <div data-testid="paper-viewer" className="rounded-3xl border border-border bg-card overflow-hidden">
      <div className="p-6 md:p-8 border-b border-border">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h2 className="text-2xl font-bold tracking-tight" data-testid="paper-title">{paper.title}</h2>
            <div className="mt-2 flex flex-wrap gap-2">
              <Badge variant="secondary">{paper.exam_board}</Badge>
              <Badge variant="secondary">{paper.qualification}</Badge>
              <Badge variant="secondary">Paper {paper.paper_number}</Badge>
              <Badge variant="secondary">{paper.time_allowed ? `${paper.time_allowed} min` : "Untimed"}</Badge>
              <Badge variant="secondary">{paper.total_marks} marks</Badge>
              <Badge className="bg-primary/15 text-primary hover:bg-primary/15">{paper.difficulty}</Badge>
              {paper.predicted && <Badge className="bg-purple-500/15 text-purple-400 hover:bg-purple-500/15">Predicted Paper</Badge>}
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button asChild size="sm" className="rounded-full glow-btn" data-testid="sit-paper-btn">
              <Link to={`/exam/${paper.id}`}><PlayCircle className="h-4 w-4 mr-1.5" /> Sit This Paper</Link>
            </Button>
            <Button variant="outline" size="sm" data-testid="bookmark-paper-btn" onClick={toggleBookmark} className="rounded-full">
              <Bookmark className={`h-4 w-4 mr-1.5 ${bookmarked ? "fill-primary text-primary" : ""}`} /> {bookmarked ? "Saved" : "Bookmark"}
            </Button>
            <Button variant="outline" size="sm" data-testid="download-paper-pdf-btn" onClick={() => downloadPdf(paper.id, "paper", paper.subject)} className="rounded-full">
              <Download className="h-4 w-4 mr-1.5" /> Paper PDF
            </Button>
            <Button variant="outline" size="sm" data-testid="download-markscheme-pdf-btn" onClick={() => downloadPdf(paper.id, "markscheme", paper.subject)} className="rounded-full">
              <Download className="h-4 w-4 mr-1.5" /> Mark Scheme PDF
            </Button>
          </div>
        </div>
        {paper.instructions?.length > 0 && (
          <div className="mt-5 rounded-2xl bg-secondary p-4">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground mb-2">Instructions</p>
            <ul className="text-sm text-muted-foreground space-y-1 list-disc pl-4">
              {paper.instructions.map((i, idx) => <li key={idx}>{i}</li>)}
            </ul>
          </div>
        )}
      </div>

      <Tabs defaultValue="questions" className="p-6 md:p-8">
        <TabsList className="rounded-full">
          <TabsTrigger value="questions" data-testid="tab-questions" className="rounded-full"><FileText className="h-3.5 w-3.5 mr-1.5" />Questions</TabsTrigger>
          <TabsTrigger value="markscheme" data-testid="tab-markscheme" className="rounded-full"><ListChecks className="h-3.5 w-3.5 mr-1.5" />Mark Scheme</TabsTrigger>
          <TabsTrigger value="answers" data-testid="tab-answers" className="rounded-full"><GraduationCap className="h-3.5 w-3.5 mr-1.5" />Model Answers</TabsTrigger>
          <TabsTrigger value="commentary" data-testid="tab-commentary" className="rounded-full"><MessageSquareQuote className="h-3.5 w-3.5 mr-1.5" />Commentary</TabsTrigger>
        </TabsList>

        {["questions", "markscheme", "answers", "commentary"].map((tab) => (
          <TabsContent key={tab} value={tab} className="mt-6 space-y-5">
            {paper.questions?.map((q) => (
              <div key={q.number} className="rounded-2xl border border-border p-5" data-testid={`question-${q.number}-${tab}`}>
                <div className="flex items-center justify-between mb-3">
                  <span className="font-semibold font-display">Question {q.number}</span>
                  <div className="flex gap-2 items-center">
                    <Badge variant="outline" className="text-xs">{q.topic}</Badge>
                    <Badge variant="outline" className="text-xs">{q.command_word}</Badge>
                    <Badge className="bg-primary/15 text-primary hover:bg-primary/15 text-xs">{q.marks} marks</Badge>
                  </div>
                </div>
                {tab === "questions" && (
                  <>
                    {q.figure_description && <p className="text-xs italic text-muted-foreground mb-2 rounded-xl bg-secondary p-3">Figure {q.number}: {q.figure_description}</p>}
                    <p className="text-sm leading-relaxed whitespace-pre-line">{q.text}</p>
                    {editable && (
                      <div className="mt-4 flex flex-wrap gap-2 border-t border-border pt-3">
                        <Button size="sm" variant="outline" className="rounded-full text-xs h-8" data-testid={`replace-question-${q.number}`}
                          disabled={busy === q.number} onClick={() => replaceQuestion(q.number)}>
                          {busy === q.number ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5 mr-1.5" />}
                          New question
                        </Button>
                        <Button size="sm" variant="ghost" className="rounded-full text-xs h-8 text-muted-foreground hover:text-destructive" data-testid={`remove-question-${q.number}`}
                          disabled={busy === q.number} onClick={() => removeQuestion(q.number)}>
                          <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Remove
                        </Button>
                        <span className="text-[11px] text-muted-foreground self-center">"New question" keeps the same topic &amp; marks.</span>
                      </div>
                    )}
                  </>
                )}
                {tab === "markscheme" && <p className="text-sm leading-relaxed whitespace-pre-line text-muted-foreground">{ms[q.number]?.scheme || "—"}</p>}
                {tab === "answers" && <p className="text-sm leading-relaxed whitespace-pre-line text-muted-foreground">{ma[q.number]?.answer || "—"}</p>}
                {tab === "commentary" && <p className="text-sm leading-relaxed whitespace-pre-line text-muted-foreground">{ec[q.number]?.commentary || "—"}</p>}
              </div>
            ))}
            {tab === "questions" && editable && (
              <div className="rounded-2xl border border-dashed border-border p-5" data-testid="add-question-panel">
                <p className="text-sm font-semibold mb-1">Add a question</p>
                <p className="text-xs text-muted-foreground mb-3">Removed something you haven't covered yet? Top your paper back up with a question on a topic you've studied.</p>
                <div className="flex flex-col sm:flex-row gap-2">
                  <Select value={addTopic} onValueChange={setAddTopic}>
                    <SelectTrigger className="sm:flex-1" data-testid="add-question-topic-select"><SelectValue placeholder="Topic" /></SelectTrigger>
                    <SelectContent>{topicOptions.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                  <Select value={addMarks} onValueChange={setAddMarks}>
                    <SelectTrigger className="sm:w-40" data-testid="add-question-marks-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{["2", "4", "6", "8", "16"].map((m) => <SelectItem key={m} value={m}>{m} marks</SelectItem>)}</SelectContent>
                  </Select>
                  <Button size="sm" className="rounded-full glow-btn" data-testid="add-question-btn" disabled={adding || !addTopic} onClick={addQuestion}>
                    {adding ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" />Adding…</> : <><Plus className="h-4 w-4 mr-1.5" />Add question</>}
                  </Button>
                </div>
              </div>
            )}
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
};
